import pluralize from "pluralize";
import { useCallback, useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { ActionButton, Card, CardRowSimple, CardSection, DialogComponent, notify } from "widgets";
import { useShallow } from "zustand/shallow";

import { syntenyViewerDefaultTracks } from "../../../settings.ts";
import { useGenomeViewerStore } from "../../../store/store.ts";
import type { MainThreadHoverMessage } from "../../../types.ts";
import { align, type MemberItem } from "../../../utils/align.ts";
import { FetchError } from "../../../utils/errors.ts";
import { getTrackTypeBySyntenyType } from "../../../utils/type-conversion.ts";
import { handleFeaturePopover } from "../utils/handle-feature-popover.ts";
import { getSequenceFamilyMembersByGenomeOrThrow } from "../utils/process-genome-data.ts";
import { Skeleton } from "./Skelton.tsx";
import { TrackRegionTooltip } from "./TrackRegionTooltip.tsx";

export function RegionInfoPopover() {
  const { showBoundary } = useErrorBoundary();
  const [sameQty, setSameQty] = useState<number>();
  const show = useGenomeViewerStore((state) => state.showFeaturePopover);
  const memberItem: (MemberItem & Partial<MainThreadHoverMessage>) | undefined = useGenomeViewerStore(
    useShallow((state) => {
      if (state.contextualMenuData?.id && state.contextualMenuData.trackId && state.contextualMenuData.orthologId) {
        const track = state.tracks.get(state.contextualMenuData.trackId);

        if (track) {
          const group = state.trackGroups.get(track?.groupId);

          if (group) {
            return {
              ...state.contextualMenuData,
              featureId: state.contextualMenuData.id, // TypeScript is dumb, we need to reset it so that he's happy.
              trackId: state.contextualMenuData.trackId, // TypeScript is dumb, we need to reset it so that he's happy.
              familyId: state.contextualMenuData.orthologId,
              contigId: group.contigId,
              trackGroupId: group.groupId,
              start: state.contextualMenuData.start,
            } satisfies MemberItem;
          }
        }
      }

      return undefined;
    }),
  );
  const activeSyntenyType = useGenomeViewerStore((state) => state.activeSyntenyType);
  const syntenyId = useGenomeViewerStore((state) => state.syntenyOrganism.id);
  const [group] = useGenomeViewerStore(
    useShallow((state) => {
      const none = [undefined, undefined, undefined];

      if (!memberItem) return none;

      const track = state.tracks.get(memberItem.trackId);
      const group = track ? state.trackGroups.get(track.groupId) : undefined;

      if (!track || !group) return none;

      return [state.trackGroups.get(track.groupId), track.groupId, track];
    }),
  );

  const currentTrackType = useGenomeViewerStore(
    useShallow((state) => {
      const trackId = state.contextualMenuData?.trackId;
      return trackId ? state.tracks.get(trackId)?.trackType : undefined;
    }),
  );

  // const [paralogs, setParalogs] = useState<GenomeMember[]>([]);
  const [hasOrthologs, setHasOrthologs] = useState(true); // In relationship with isLoading
  const [isLoading, setIsLoading] = useState(true); // We'll load

  useEffect(() => {
    if (show) {
      // reset
      setHasOrthologs(true); // Align by default
      setIsLoading(true);

      async function load(): Promise<[boolean, number]> {
        if (memberItem?.trackId && memberItem.familyId && group?.organismId && syntenyId) {
          const homologs = await getSequenceFamilyMembersByGenomeOrThrow(syntenyId, memberItem.familyId);
          const paralogsData = (homologs?.get(group.organismId) ?? []).filter(
            (m) => m.locationId !== memberItem.featureId,
          );

          const orthologs = Array.from(homologs.values())
            .flatMap((members) => members.filter((m) => ![...paralogsData.values()].includes(m)))
            .filter((o) => o.organismId !== group.organismId);

          return [orthologs.length > 0, homologs.size];
        }

        return [false, 0];
      }

      load()
        .then(([hasOrthologs, qty]) => {
          setHasOrthologs(hasOrthologs);
          setSameQty(qty);
          setIsLoading(false);
        })
        .catch((error: Error | FetchError) => {
          if (error instanceof FetchError && error.statusCode === 401) {
            showBoundary(error);
          }

          notify.error({ title: "Could not load feature info", children: error.message });
        });
    }
  }, [
    syntenyId,
    group?.organismId,
    memberItem?.familyId,
    memberItem?.featureId,
    memberItem?.trackId,
    show,
    showBoundary,
  ]);

  const close = useCallback(() => {
    const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;
    Object.assign(contextualMenu.style, {
      display: "none",
    });
    useGenomeViewerStore.setState({ showFeaturePopover: false, contextualMenuData: undefined });
  }, []);

  useEffect(() => {
    document.body.addEventListener("pointerdown", handleFeaturePopover, true);

    return () => {
      document.body.removeEventListener("pointerdown", handleFeaturePopover, true);
    };
  }, []);

  const handleAlign = useCallback(async () => {
    if (memberItem) {
      await align(memberItem);
    }
    close();
  }, [close, memberItem]);

  const showSkeleton = isLoading || !memberItem;

  return (
    <div
      id="genome-viewer-contextual-menu"
      data-testid="genome-viewer-contextual-menu"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        display: "none",
        zIndex: 10,
      }}
    >
      {!!memberItem && (
        <DialogComponent
          title={memberItem.name}
          subtitle={syntenyViewerDefaultTracks.find((trackType) => trackType.type === currentTrackType)?.name}
          asPanelHeader
          contentStyle={{ height: "fit-content" }}
          width={300}
        >
          <Card noBorder noPadding>
            {showSkeleton ? (
              <CardSection>
                <Skeleton />
              </CardSection>
            ) : (
              <>
                <CardSection>
                  <TrackRegionTooltip data={memberItem} />
                </CardSection>
                {!!activeSyntenyType && getTrackTypeBySyntenyType(activeSyntenyType) === currentTrackType && (
                  <CardRowSimple label={`Segment present in ${sameQty} ${pluralize("Genome", sameQty)}`}>
                    <ActionButton
                      isDisabled={!hasOrthologs}
                      onPress={handleAlign}
                      isLoading={isLoading}
                      label={hasOrthologs || isLoading ? "Align Genomes" : "No Syntenic Regions in Other Genomes"}
                      style={{ marginTop: 10 }}
                    />
                  </CardRowSimple>
                )}
              </>
            )}
          </Card>
        </DialogComponent>
      )}
    </div>
  );
}
